/* ════════════════════════════════════════════════════════════════════════
   Meal Suggestion Intelligence — "What should we have for dinner?"
   Pure derivation from: Family Profiles (via foodIntelligence) + Family
   Recipe Book + Pantry + time tonight + this week's plan.
   Nothing persisted. No recipes generated. Deterministic and explainable.

   Ranking (documented):
     HARD GATE  allergy / intolerance / hard dietary conflict → excluded
     +2 per favourite match (capped +4)   +1 recipe marked family favourite
     +2 pantry ready · +0.5 nearly · −1 shop first
     +1 fits selected time · −2 over selected time (only when time known)
     −3 "won't eat" · −1.5 dislike/sensory (soft — shown with a caution)
     −1 already planned this week (weekly plan is the only reliable history)
   ════════════════════════════════════════════════════════════════════════ */

import { evaluateMealForHousehold } from "./foodIntelligence";

const norm = (s) => String(s || "").toLowerCase().trim();
const STOP_WORDS = new Set(["the", "and", "with", "fresh", "large", "small", "chopped", "diced", "sliced", "cup", "cups", "tbsp", "tsp", "grams", "leg", "fillets", "fillet", "thighs", "breast"]);

/* Words worth matching from a pantry item name or ingredient line. */
function keyWords(text) {
  return norm(text).split(/[^a-z]+/).filter(w => w.length >= 3 && !STOP_WORDS.has(w));
}

/* ── Pantry readiness — approximate, honest, derived from live pantry ── */
export function pantryReadiness(recipe, pantryItems) {
  const ingredients = recipe?.ingredients || [];
  const items = pantryItems || [];
  if (ingredients.length === 0 || items.length === 0) {
    return { state: "unknown", missing: [], line: null };
  }
  const stocked = items
    .filter(p => p.status !== "Expired" && p.status !== "Out" && (p.quantity === undefined || p.quantity > 0))
    .flatMap(p => keyWords(p.name || p.item));
  const stockedSet = new Set(stocked);
  const missing = ingredients.filter(ing => !keyWords(ing).some(w => stockedSet.has(w)));

  if (missing.length === 0) return { state: "ready", missing, line: "You already have what you need." };
  if (missing.length === 1) return { state: "nearly", missing, line: "You're one ingredient short." };
  if (missing.length === 2) return { state: "nearly", missing, line: "You may need to check two ingredients." };
  return { state: "shop", missing, line: "A few ingredients to pick up first." };
}

const totalTimeOf = (recipe) => {
  const t = (recipe.prepTime || 0) + (recipe.cookTime || 0);
  return t > 0 ? t : null;
};

/* Is this recipe already on the week's plan (the only reliable history)? */
function plannedThisWeek(recipe, weeklyMealPlan) {
  return (weeklyMealPlan || []).some(d => {
    if (d.dinnerRecipeId === recipe.id || norm(d.dinner) === norm(recipe.title)) return true;
    return Object.values(d.slots || {}).some(s => s?.recipeId === recipe.id || norm(s?.title) === norm(recipe.title));
  });
}

export function evaluateMealCandidate(recipe, ctx, { pantryItems, timeLimit, weeklyMealPlan }) {
  const suitability = evaluateMealForHousehold({ recipe }, ctx);
  if (suitability.status === "conflict") return { excluded: true };

  let score = 0;
  const whyParts = [];
  const cautions = [];
  const meta = [];

  const time = totalTimeOf(recipe);
  if (time) meta.push(`${time} min`);

  /* Favourites — never override safety (conflicts already excluded). */
  const favs = suitability.positives || [];
  if (favs.length > 0) {
    score += Math.min(favs.length * 2, 4);
    const names = [...new Set(favs.flatMap(f => f.members))];
    whyParts.push(names.length > 1
      ? `${names.slice(0, 2).join(" and ")} both like what's in this one.`
      : `${names[0]} likes this one.`);
  }
  if (recipe.favourite) {
    score += 1;
    meta.push("family favourite");
    if (favs.length === 0) whyParts.push("It's one of the family's favourites.");
  }

  /* Soft conflicts — honest cautions, strong penalties. */
  (suitability.checks || []).forEach(c => {
    score -= c.kind === "wontEat" ? 3 : 1.5;
  });
  if (suitability.status === "check" && suitability.line) cautions.push(suitability.line);

  /* Pantry — only claim what the data supports. */
  const pantry = pantryReadiness(recipe, pantryItems);
  if (pantry.state === "ready") { score += 2; meta.push("pantry ready"); whyParts.push("You already have what you need."); }
  else if (pantry.state === "nearly") { score += 0.5; whyParts.push(pantry.line); cautions.push(pantry.line); }
  else if (pantry.state === "shop") { score -= 1; whyParts.push(pantry.line); cautions.push(pantry.line); }

  /* Time — no fabricated cook times. */
  if (timeLimit && time) {
    if (time <= timeLimit) { score += 1; whyParts.push("It fits the time you've got tonight."); }
    else { score -= 2; cautions.push(`Takes about ${time} minutes — longer than the time you picked.`); }
  }

  /* Servings — informational only, never invented. */
  const eating = (ctx.memberIds || []).length;
  if (recipe.serves && eating > recipe.serves) {
    cautions.push(`Serves ${recipe.serves} — you may need to scale it up.`);
  }

  /* Repetition — weekly plan only; no fake long-term history. */
  if (plannedThisWeek(recipe, weeklyMealPlan)) score -= 1;

  /* Primary line for the row. */
  let line;
  if (suitability.status === "good" && ctx.hasAnyFoodInfo) {
    line = pantry.state === "ready"
      ? "Everyone you're cooking for is covered, and you already have what you need."
      : "Everyone you're cooking for is covered.";
  } else if (suitability.status === "check") {
    line = suitability.line;
  } else {
    line = pantry.line || "A straightforward one from your Recipe Book.";
  }

  return {
    excluded: false,
    recipeId: recipe.id,
    title: recipe.title,
    score,
    time,
    meta: meta.join(" · "),
    line,
    cautions,
    why: buildWhy(suitability, whyParts, ctx),
    hasWontEat: (suitability.checks || []).some(c => c.kind === "wontEat"),
  };
}

function buildWhy(suitability, whyParts, ctx) {
  const parts = [...whyParts];
  if (suitability.status === "good" && ctx.hasAnyFoodInfo) {
    parts.unshift("No known conflicts were found from the information currently saved in your Family Profiles.");
  }
  if (parts.length === 0) parts.push("A choice from your own Family Recipe Book.");
  return parts.join(" ");
}

/* ── Main entry — up to three honest picks, never padded. ── */
export function suggestMealsForTonight({ recipes, ctx, pantryItems, timeLimit, weeklyMealPlan }) {
  const book = recipes || [];
  if (book.length === 0) return { state: "empty-book", picks: [] };

  const evaluated = book
    .map(r => evaluateMealCandidate(r, ctx, { pantryItems, timeLimit, weeklyMealPlan }))
    .filter(e => !e.excluded)
    .sort((a, b) => b.score - a.score);

  if (evaluated.length === 0) return { state: "no-fit", picks: [] };

  /* Prefer to omit "won't eat" recipes when better choices exist. */
  const clean = evaluated.filter(e => !e.hasWontEat);
  const pool = clean.length >= Math.min(3, evaluated.length) ? clean : evaluated;
  const picks = pool.slice(0, 3);

  return { state: picks.length > 0 ? "picks" : "no-fit", picks };
}
